import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CurrencyConvert,
  CurrencyConvertHistory,
  CurrencyIcon,
  decodeID,
  formatDisplayLongDate,
  getFromStorage,
  getPriceHelper,
} from "../../utils/Helper";
import { StorageConstant } from "../../constants/Constants";
import {
  parseChargeLineAmount,
  parseOtherChargesLines,
} from "../myquotation/view-quotation/helpers";
import {
  buildOrderDeliveryAddressLines,
  getOrderResidenceLabel,
  getOrderShippingInstructions,
} from "../../utils/quotationCheckout";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import PageLoadingShell from "../layout/PageLoadingShell";
import ListPageSkeleton from "../layout/ListPageSkeleton";
import { LanguageConstants } from "../../constants/LanguageConstants";
import {
  canRetryOnlinePayment,
  getCustomerOrderStatusColorClass,
  getCustomerOrderStatusKey,
  getCustomerPaymentStatusColorClass,
  getCustomerPaymentStatusKey,
} from "../../utils/orderStatus";
import { Routes as AppRoutes } from "../../constants/RouteConstants";
import { Modal } from "react-bootstrap";
import Shippingdetails from "../../Modal/Shippingdetails";
import OrderCancelMessage from "../../Modal/OrderCancelMessage";
import OrderCancel from "../../Modal/OrderCancel";
import OrderSurveyMessage from "../../Modal/OrderSurveyMessage";
import SurveyModal from "../../Modal/SurveyModal";
import {
  ORDER_DETAIL_SURVEY_BUTTON_ENABLED,
  ORDER_DETAIL_SURVEY_PROMPT_ENABLED,
} from "../../constants/Constants";
import truckIcon from "../../Assets/images/Truck.svg";
import listIcon from "../../Assets/images/list-icon.svg";
import gridIcon from "../../Assets/images/grid-icon.svg";
import downloadButtonIcon from "../../Assets/images/download button.svg";
import { toast } from "react-toastify";
import { openOrderPdfInNewTab } from "../../utils/openQuotationPdf";
import "./OrderDetail.scss";
import "./OrderDetailTableHeadings.scss";

type ItemsView = "list" | "table";

function orderStatusIsDelivered(status: unknown): boolean {
  return String(status ?? "") === "2";
}

function surveyFlagIs(value: unknown, expected: number): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n === expected;
}

function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const orderView = useMakeRequest();
  const [currency, setCurrency] = useState<"USD" | "EUR">("USD");
  const [itemsView, setItemsView] = useState<ItemsView>("table");
  const [show, setShow] = useState(false);
  const [messageshow, setMessageShow] = useState(false);
  const [cancelShow, setCancelShow] = useState(false);
  const [surveymessageShow, setSurveymessageShow] = useState(false);
  const [surveyShow, setSurveyShow] = useState(false);
  const [otherChargesModalOpen, setOtherChargesModalOpen] = useState(false);
  const [apiRefresh, setApiRefresh] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    setHasLoadedOnce(false);
    if (!id) return;
    const payload = {
      order_id: decodeID(decodeURIComponent(String(id))),
    };
    orderView.makeRequest({
      payload,
      url: ApiConstants?.OrderDetail,
      toastStatus: false,
    });
  }, [id, apiRefresh]);

  useEffect(() => {
    if (!orderView.loading && orderView?.status) {
      setHasLoadedOnce(true);
    }
  }, [orderView.loading, orderView?.status]);

  const showInitialPageLoader =
    Boolean(id) &&
    !hasLoadedOnce &&
    (orderView.loading || (!orderView.status && orderView.error == null));

  const o = orderView?.data?.data;
  const quotation = o?.quotation_detail;
  const quotes: any[] = useMemo(
    () => quotation?.quotes_detail ?? [],
    [quotation?.quotes_detail],
  );
  const priceMode = currency === "EUR" ? "EUR" : "USD";

  const numBk = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const otherChargesLines = useMemo(
    () => parseOtherChargesLines(quotation?.charges_reason),
    [quotation?.charges_reason],
  );
  const otherChargesTotal = numBk(quotation?.other_charges);
  const hasOtherChargesDetail =
    otherChargesLines.length > 0 || otherChargesTotal > 0;

  const getTaxNumeric = (q: any) => {
    if (q?.tax_amount && q?.total_amount) {
      const base =
        Number(q?.total_shipping_price) +
        Number(q?.total_product_price) +
        Number(q?.other_charges || 0);
      return (base * Number(q?.tax_amount)) / 100;
    }
    return 0;
  };

  const formatOrderDate = (d: any) => formatDisplayLongDate(d, "—");

  const lineExtended = (row: any) =>
    Number(row?.quantity ?? 0) * Number(row?.per_product_price ?? 0);

  const customerStatusPill = (colorClass: string, label: React.ReactNode) => (
    <span
      className={`order-detail-page__pill customer-order-status ${colorClass}`}
      role="status"
    >
      {label}
    </span>
  );

  const orderStatusLabel = (
    key: ReturnType<typeof getCustomerOrderStatusKey>,
  ) => {
    if (key === "pending") return LanguageConstants?.PENDING;
    if (key === "in_process") return LanguageConstants?.Progress;
    if (key === "in_transit") return LanguageConstants?.INTRANSIT;
    if (key === "delivered") return LanguageConstants?.DELIVERD;
    return LanguageConstants?.CANVELLED;
  };

  const paymentStatusLabel = (
    key: ReturnType<typeof getCustomerPaymentStatusKey>,
  ) => {
    if (key === "wt_pending") {
      return getFromStorage({ key: StorageConstant?.Translate }) === "spa"
        ? "TB Pendiente"
        : LanguageConstants?.ORDER_PAYMENT_WT_PENDING;
    }
    if (key === "wt_refund_pending") {
      return LanguageConstants?.ORDER_PAYMENT_WT_REFUND_PENDING;
    }
    if (key === "declined") return LanguageConstants?.ORDER_PAYMENT_DECLINED;
    if (key === "pending" || key === "processing") {
      return LanguageConstants?.PENDING;
    }
    if (key === "paid_cc") {
      return `${LanguageConstants?.PAID} ${LanguageConstants?.CC}`;
    }
    if (key === "paid_wt") {
      return `${LanguageConstants?.PAID} ${LanguageConstants?.WT}`;
    }
    if (key === "refunded") return LanguageConstants?.REFUND;
    return LanguageConstants?.REJECT;
  };

  const deliveryPill = () => {
    const key = getCustomerOrderStatusKey(o);
    return customerStatusPill(
      getCustomerOrderStatusColorClass(key),
      orderStatusLabel(key),
    );
  };

  const paymentPill = () => {
    const key = getCustomerPaymentStatusKey(o);
    return customerStatusPill(
      getCustomerPaymentStatusColorClass(key),
      paymentStatusLabel(key),
    );
  };

  const showContinueToPayment = canRetryOnlinePayment(o);

  const handleContinueToPayment = () => {
    if (!o?.uuid || !id) return;
    navigate(
      AppRoutes.OrderCheckout.replace(":id", encodeURIComponent(String(id))),
    );
  };

  const handleDownloadPdf = () => {
    if (!id) return;
    const decodedOrderId = decodeID(decodeURIComponent(String(id)));
    if (!decodedOrderId) {
      toast.error("Could not load PDF.");
      return;
    }
    void openOrderPdfInNewTab(decodedOrderId);
  };

  useEffect(() => {
    setSurveymessageShow(false);
    setSurveyShow(false);
  }, [id]);

  useEffect(() => {
    const order = orderView?.data?.data;
    if (!orderView?.status || !order) {
      return;
    }

    const shouldPromptSurvey =
      ORDER_DETAIL_SURVEY_PROMPT_ENABLED &&
      orderStatusIsDelivered(order.status) &&
      surveyFlagIs(order.is_survey, 0);

    setSurveymessageShow(shouldPromptSurvey);
  }, [orderView?.status, orderView?.data]);

  return (
    <>
      <PageLoadingShell
        loading={showInitialPageLoader}
        skeleton={<ListPageSkeleton />}
        className="head-space"
      >
        <div className="inner-content">
          <div className="order-detail-page">
            <div className="order-detail-page__stage">
              <div className="order-detail-page__shell">
                {o ? (
                  <>
                    <header className="order-detail-page__summary">
                      <div className="order-detail-page__summaryTop">
                        <div className="order-detail-page__summaryLeft">
                          <div className="order-detail-page__summaryTitle">
                            {LanguageConstants?.ORDER_DETAILS_HEAD}: {o?.uuid}
                          </div>
                          <p className="order-detail-page__summaryLine">
                            {LanguageConstants?.DATE}:{" "}
                            {formatOrderDate(o?.created_at)}
                          </p>
                          <p className="order-detail-page__summaryLine">
                            {LanguageConstants?.VIEW_QUOTE_ITEMS_COUNT}{" "}
                            {quotes.length}
                          </p>
                        </div>
                        <div className="order-detail-page__summaryActions">
                          {paymentPill()}
                          {deliveryPill()}
                          <button
                            type="button"
                            className="order-detail-page__downloadPdfBtn"
                            onClick={handleDownloadPdf}
                          >
                            <img
                              src={downloadButtonIcon}
                              alt=""
                              className="order-detail-page__downloadPdfBtnIcon"
                              width={22}
                              height={22}
                              draggable={false}
                            />
                            {LanguageConstants?.DOWNLOAD_PDF}
                          </button>
                          <button
                            type="button"
                            className="order-detail-page__listViewBtn"
                            aria-pressed={itemsView === "list"}
                            title={
                              itemsView === "table"
                                ? LanguageConstants?.GRID_VIEW
                                : LanguageConstants?.LIST_VIEW
                            }
                            onClick={() =>
                              setItemsView((v) =>
                                v === "table" ? "list" : "table",
                              )
                            }
                          >
                            {itemsView === "table" ? (
                              <img src={gridIcon} alt="Grid View" />
                            ) : (
                              <img src={listIcon} alt="List View" />
                            )}
                            {itemsView === "table"
                              ? LanguageConstants?.GRID_VIEW
                              : LanguageConstants?.LIST_VIEW}
                          </button>
                          
                        </div>
                      </div>
                    </header>

                    <div className="order-detail-page__grid">
                      <section className="order-detail-page__card order-detail-page__priceCard">
                        <div className="order-detail-page__cardHeadRow order-detail-page__cardHeadRow--divider">
                          <h2 className="order-detail-page__cardTitle">
                            {LanguageConstants?.PRICE_BRACKDOWN}
                          </h2>
                          <label className="order-detail-page__euroToggle">
                            <span className="order-detail-page__euroSwitch">
                              <input
                                type="checkbox"
                                checked={currency === "EUR"}
                                onChange={(e) =>
                                  setCurrency(e.target.checked ? "EUR" : "USD")
                                }
                                aria-label={LanguageConstants?.CONVERTINPISO}
                              />
                              <span
                                className="order-detail-page__euroSwitchTrack"
                                aria-hidden
                              />
                            </span>
                            <span className="order-detail-page__euroToggleText">
                              {LanguageConstants?.CONVERTINPISO}
                            </span>
                          </label>
                        </div>
                        <div className="order-detail-priceRows">
                          <div className="order-detail-priceRows__row">
                            <span className="order-detail-priceRows__label">
                              {LanguageConstants?.TOTALPRODUCTAMOUNT}
                            </span>
                            <span className="order-detail-priceRows__value">
                              {getPriceHelper(
                                CurrencyConvert(
                                  quotation?.total_product_price,
                                  priceMode,
                                ),
                              )}
                            </span>
                          </div>
                          <div className="order-detail-priceRows__row">
                            <span className="order-detail-priceRows__label">
                              {LanguageConstants?.TOTALSHIIPNGAMOUNT}
                            </span>
                            <span className="order-detail-priceRows__value">
                              {getPriceHelper(
                                CurrencyConvert(
                                  quotation?.total_shipping_price,
                                  priceMode,
                                ),
                              )}
                            </span>
                          </div>
                          <div className="order-detail-priceRows__row">
                            <span className="order-detail-priceRows__label">
                              <span>
                                {LanguageConstants?.OTHER}{" "}
                                {LanguageConstants?.CHARGES}
                              </span>
                              <button
                                type="button"
                                className={`order-detail-priceRows__info${
                                  hasOtherChargesDetail
                                    ? ""
                                    : " order-detail-priceRows__info--disabled"
                                }`}
                                disabled={!hasOtherChargesDetail}
                                aria-disabled={!hasOtherChargesDetail}
                                title={
                                  hasOtherChargesDetail
                                    ? LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_BTN ??
                                      "View other charges breakdown"
                                    : LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_NONE ??
                                      "No other charges details"
                                }
                                aria-label={
                                  LanguageConstants?.Charges_reason ??
                                  "Other charges details"
                                }
                                onClick={() => {
                                  if (hasOtherChargesDetail) {
                                    setOtherChargesModalOpen(true);
                                  }
                                }}
                              >
                                <i className="icon-info"></i>
                              </button>
                            </span>
                            <span className="order-detail-priceRows__value">
                              {quotation?.other_charges
                                ? getPriceHelper(
                                    CurrencyConvert(
                                      quotation.other_charges,
                                      priceMode,
                                    ),
                                  )
                                : getPriceHelper(
                                    CurrencyConvert(0, priceMode),
                                  )}
                            </span>
                          </div>
                          <div className="order-detail-priceRows__row">
                            <span className="order-detail-priceRows__label">
                              {LanguageConstants?.TOTALTAX}
                            </span>
                            <span className="order-detail-priceRows__value">
                              {quotation?.tax_amount
                                ? getPriceHelper(
                                    CurrencyConvert(
                                      getTaxNumeric(quotation).toFixed(2),
                                      priceMode,
                                    ),
                                  )
                                : getPriceHelper(
                                    CurrencyConvert(0, priceMode),
                                  )}
                            </span>
                          </div>
                          <div className="order-detail-priceRows__foot">
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <span className="order-detail-priceRows__grandLabel">
                                {LanguageConstants?.GRANDTOTAL}
                              </span>
                              <div className="order-detail-priceRows__grandWrap">
                                <p className="order-detail-priceRows__grandNote">
                                  {priceMode === "EUR"
                                    ? LanguageConstants?.VIEW_QUOTE_FOLLOW_EUR ??
                                      "Following price is in EUR"
                                    : LanguageConstants?.PRIZEMESSAGE}
                                </p>
                                <p className="order-detail-priceRows__grandAmt">
                                  {getPriceHelper(
                                    CurrencyConvert(
                                      quotation?.total_amount,
                                      priceMode,
                                    ),
                                  )}
                                </p>
                              </div>
                            </div>
                            {showContinueToPayment ? (
                              <div className="row mt-5">
                                <div className="col-11 col-sm-7 mx-auto">
                                  <button
                                    type="button"
                                    className="round-btn w-100"
                                    onClick={handleContinueToPayment}
                                  >
                                    {LanguageConstants?.CONTINUE_TO_PAYMENT ??
                                      "Continue to Payment"}
                                  </button>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </section>

                      <section className="order-detail-page__card order-detail-page__shippingCard">
                        <div className="order-detail-page__shippingHead">
                          <img src={truckIcon} alt="" />
                          {LanguageConstants?.MANUAL_QUOTE_SHIPPING_DETAILS}
                        </div>
                        <div className="order-detail-page__shippingBody">
                          <div className="order-detail-page__shippingBody__row">
                            <div className="order-detail-page__shippingBody__dl">
                              {LanguageConstants?.MANUAL_QUOTE_DELIVERY_SECTION}
                            </div>
                            <dd className="order-detail-page__shippingBody__dd">
                              {buildOrderDeliveryAddressLines(o)}
                            </dd>
                          </div>
                          <div className="order-detail-page__shippingBody__row">
                            <div className="order-detail-page__shippingBody__dl">
                              {LanguageConstants?.MANUAL_QUOTE_INSTRUCTIONS}
                            </div>
                            <dd className="order-detail-page__shippingBody__dd">
                              {getOrderShippingInstructions(o)}
                            </dd>
                          </div>
                          <div className="order-detail-page__shippingBody__row">
                            <div className="order-detail-page__shippingBody__dl">
                              {LanguageConstants?.MANUAL_QUOTE_RESIDENCE}
                            </div>
                            <dd className="order-detail-page__shippingBody__dd">
                              {getOrderResidenceLabel(o)}
                            </dd>
                          </div>
                          <div className="order-detail-page__shippingBody__row">
                            <div className="order-detail-page__shippingBody__dl">
                              {LanguageConstants?.MANUAL_QUOTE_SHIPPING_METHOD_SECTION}
                            </div>
                            <dd className="order-detail-page__shippingBody__dd">
                              {quotation?.shipping_Trans_detail?.name || "—"}
                            </dd>
                          </div>
                        </div>
                      </section>
                    </div>

                      {itemsView === "list" ? (
                      <section className="order-detail-page__itemsCard">
                        <div className="order-detail-page__itemsScroll">
                          {quotes.map((res: any, i: number) => {
                            const itemName =
                              [res?.part_number, res?.description]
                                .map((x: any) =>
                                  x != null ? String(x).trim() : "",
                                )
                                .filter(Boolean)[0] || "—";
                            const otherBrandOnly =
                              res?.other_brand_name != null
                                ? String(res.other_brand_name).trim()
                                : "";
                            return (
                              <div
                                className="order-detail-items__row"
                                key={res?.id ?? i}
                              >
                                <div className="order-detail-items__left">
                                  <div className="order-detail-items__idx">
                                    {LanguageConstants?.Item} {i + 1}
                                  </div>
                                  {/* <p className="order-detail-items__name">
                                    {itemName}
                                  </p> */}
                                  <div className="order-detail-items__kv">
                                    <div className="order-detail-items__kvRow">
                                      {otherBrandOnly ? (
                                        <span className="order-detail-items__kvLabel">
                                          {otherBrandOnly}
                                        </span>
                                      ) : null}
                                    </div>
                                    <div className="order-detail-items__kvRow">
                                      <span className="order-detail-items__kvLabel">
                                        {LanguageConstants?.PARTNAME}
                                        {": "}
                                      </span>
                                      <span className="order-detail-items__kvValue">
                                        {res?.part_number || "—"}
                                      </span>
                                    </div>
                                    <div className="order-detail-items__kvRow">
                                      <span className="order-detail-items__kvLabel">
                                        {LanguageConstants?.SERIALNUMBER}
                                        {": "}
                                      </span>
                                      <span className="order-detail-items__kvValue">
                                        {res?.serial_number || "N/A"}
                                      </span>
                                    </div>
                                    <div className="order-detail-items__kvRow">
                                      <span className="order-detail-items__kvLabel">
                                        {LanguageConstants?.MODELNUMBER}
                                        {": "}
                                      </span>
                                      <span className="order-detail-items__kvValue">
                                        {res?.model_number || "N/A"}
                                      </span>
                                    </div>
                                    <div className="order-detail-items__kvRow">
                                      <span className="order-detail-items__kvLabel">
                                        {LanguageConstants?.SKU_LABEL}
                                        {": "}
                                      </span>
                                      <span className="order-detail-items__kvValue">
                                        {res?.sku != null && String(res.sku).trim() !== ""
                                          ? String(res.sku).trim()
                                          : "N/A"}
                                      </span>
                                    </div>
                                    <div className="order-detail-items__kvRow">
                                      <span className="order-detail-items__kvLabel">
                                        {LanguageConstants?.Description}
                                        {": "}
                                      </span>
                                      <span className="order-detail-items__kvValue">
                                        {res?.description || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="order-detail-items__right">
                                  <div>
                                    <strong>
                                      {LanguageConstants?.PRE_PRODUCT_PRICE}:{" "}
                                    </strong>
                                    {getPriceHelper(
                                      CurrencyConvert(
                                        res?.per_product_price ?? 0,
                                        priceMode,
                                      ),
                                    )}
                                  </div>
                                  <div>
                                    <strong>
                                      {LanguageConstants?.EXTED_PRICE}:{" "}
                                    </strong>
                                      {getPriceHelper(
                                        CurrencyConvert(
                                          lineExtended(res).toFixed(2),
                                          priceMode,
                                        ),
                                      )}
                                  </div>
                                  <div className="mt-auto">
                                    <strong>
                                      {LanguageConstants?.VIEW_QUOTE_ETA} {" "}
                                      {res?.eta_day != null && res?.eta_day !== ""
                                      ? `${res.eta_day} days`
                                      : "N/A"}
                                    </strong>
                                  </div>
                                  <div>
                                    <strong>
                                      {LanguageConstants?.ORDER_QTY} {" "}
                                      {res?.quantity}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section> ) : (
                      
                          <div className="manual-quote-table-wrap">
                            <table className="table manual-quote-table">
                              <thead>
                                <tr>
                                  <th className="text-center">
                                    {LanguageConstants?.Item}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.BRAND}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.ORDER_DETAIL_TABLE_PART_HASH}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.ORDER_QTY}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.Description}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.ORDER_DETAIL_TABLE_SERIAL_HASH}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.MODELNUMBER}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.SKU_LABEL}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.PRE_PRODUCT_PRICE}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.EXTED_PRICE}
                                  </th>
                                  <th className="text-center">
                                    {LanguageConstants?.VIEW_QUOTE_ETA}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {quotes.map((res: any, i: number) => (
                                  <tr key={res?.id ?? i}>
                                    <td className="text-center border-0">{i + 1}</td>
                                    {res?.is_other_brand === "1" ||
                                    res?.is_other_brand === 1 ? (
                                      <td className="text-center border-0">
                                        {res?.other_brand_name}
                                      </td>
                                    ) : (
                                      <td className="text-center border-0">
                                        {res?.brand_detail?.name}
                                      </td>
                                    )}
                                    <td className="text-center border-0">
                                      {res?.part_number ?? "—"}
                                    </td>
                                    <td className="text-center border-0">
                                      {res?.quantity}
                                    </td>
                                    {res?.description ? (
                                      <td className="text-center border-0">
                                        {res?.description}
                                      </td>
                                    ) : (
                                      <td className="text-center border-0">N/A</td>
                                    )}
                                    <td className="text-center border-0">
                                      {res?.serial_number || "N/A"}
                                    </td>
                                    <td className="text-center border-0">
                                      {res?.model_number || "N/A"}
                                    </td>
                                    <td className="text-center border-0">
                                      {res?.sku != null && String(res.sku).trim() !== ""
                                        ? String(res.sku).trim()
                                        : "N/A"}
                                    </td>
                                    <td className="text-center border-0">
                                      {getPriceHelper(
                                        CurrencyConvert(
                                          res?.per_product_price ?? 0,
                                          priceMode,
                                        ),
                                      )}
                                    </td>
                                    <td className="text-center border-0">
                                      {getPriceHelper(
                                        CurrencyConvert(
                                          lineExtended(res).toFixed(2),
                                          priceMode,
                                        ),
                                      )}
                                    </td>
                                    <td className="text-center border-0">
                                      {res?.eta_day ? res?.eta_day : "N/A"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                      )}

                    <div className="order-detail-page__actions">
                      {ORDER_DETAIL_SURVEY_BUTTON_ENABLED &&
                      surveyFlagIs(o?.is_over_all_survey, 0) &&
                      surveyFlagIs(o?.is_survey, 1) &&
                      orderStatusIsDelivered(o?.status) ? (
                        <button
                          type="button"
                          className="reject-approve-btn blue-btn"
                          onClick={() => setSurveyShow(true)}
                        >
                          {LanguageConstants?.GIVE_SURVEY}
                        </button>
                      ) : null}
                      {o?.is_cancel === "0" &&
                      (o?.status === "0" || o?.status === "1") ? (
                        <button
                          type="button"
                          className="reject-approve-btn reject"
                          onClick={() => setMessageShow(true)}
                        >
                          {LanguageConstants?.CANCEL}
                        </button>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </PageLoadingShell>

      <Modal show={show}>
        <Shippingdetails setShow={setShow} data={o} />
      </Modal>

      <Modal show={messageshow}>
        <OrderCancelMessage
          setMessageShow={setMessageShow}
          setCancelShow={setCancelShow}
          data={o}
        />
      </Modal>

      <Modal show={cancelShow}>
        <OrderCancel setCancelShow={setCancelShow} data={o} />
      </Modal>

      {ORDER_DETAIL_SURVEY_PROMPT_ENABLED ? (
        <>
          <Modal show={surveymessageShow}>
            <OrderSurveyMessage
              data={o}
              setSurveymessageShow={setSurveymessageShow}
              surveymessageShow={surveymessageShow}
              setSurveyShow={setSurveyShow}
              setApiRefresh={setApiRefresh}
            />
          </Modal>

          <Modal show={surveyShow}>
            <SurveyModal
              data={o}
              setSurveyShow={setSurveyShow}
              setApiRefresh={setApiRefresh}
            />
          </Modal>
        </>
      ) : null}

      <Modal
        show={otherChargesModalOpen}
        onHide={() => setOtherChargesModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_TITLE ??
              "Other Charges Breakdown"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="order-detail-otherChargesModal">
            {otherChargesLines.length > 0 ? (
              <ul className="order-detail-otherChargesModal__list">
                {otherChargesLines.map((line, idx) => (
                  <li
                    key={`${line.label}-${idx}`}
                    className="order-detail-otherChargesModal__row"
                  >
                    <span className="order-detail-otherChargesModal__label">
                      {line.label}
                    </span>
                    <span className="order-detail-otherChargesModal__amt">
                      {CurrencyIcon(priceMode)}
                      {getPriceHelper(
                        CurrencyConvertHistory(
                          parseChargeLineAmount(line.amount),
                          priceMode,
                        ),
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="order-detail-otherChargesModal__empty">
                {LanguageConstants?.VIEW_QUOTE_OTHER_CHARGES_EMPTY ??
                  "No itemized charges recorded."}
              </p>
            )}
            <div className="order-detail-otherChargesModal__total">
              <span>
                {LanguageConstants?.OTHER} {LanguageConstants?.CHARGES}
              </span>
              <span className="order-detail-otherChargesModal__totalAmt">
                {CurrencyIcon(priceMode)}
                {getPriceHelper(
                  CurrencyConvertHistory(otherChargesTotal, priceMode),
                )}
              </span>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OrderDetail;
